// SCR-FND-02 First-run setup (frozen P0 mockup, ADR-083, ADR-086). Shown once, before any account exists.
// Choosing the language switches the screen at once. Country, currency and time zone are fixed for this business.
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chips, Field, FormGrid, Input, Notice } from '../../design/kit';
import { Refusal, setupBusiness, signIn } from '../../data/auth';
import { applyLanguage, languages, type Language } from '../../i18n';
import { messageKey } from '../../lib/errors';
import { businessToday } from '../../lib/format';

const LANGUAGE_NAMES: Record<Language, string> = { ar: 'العربية', fr: 'Français', en: 'English' };
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Errors = Partial<Record<'businessName' | 'startDate' | 'ownerName' | 'email' | 'password', string>>;

export function FirstRun({ language, onDone }: { language: Language; onDone: () => void }) {
  const { t } = useTranslation();
  const [lang, setLang] = useState<Language>(language);
  const [businessName, setBusinessName] = useState('');
  const [startDate, setStartDate] = useState(businessToday());
  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [refusal, setRefusal] = useState<string>();
  const [busy, setBusy] = useState(false);

  const pickLanguage = (id: string) => {
    const l = id as Language;
    setLang(l);
    void applyLanguage(l);
  };

  // The server decides; these checks only give instant feedback with the same rules.
  const validate = (): Errors => {
    const e: Errors = {};
    if (!businessName.trim()) e.businessName = t('err.required');
    if (!startDate) e.startDate = t('err.required');
    else if (startDate > businessToday()) e.startDate = t('err.future');
    if (!ownerName.trim()) e.ownerName = t('err.required');
    if (!EMAIL.test(email.trim())) e.email = t('err.email');
    if (password.length < 8) e.password = t('err.weak');
    return e;
  };

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (busy) return;
    const e = validate();
    setErrors(e);
    setRefusal(undefined);
    if (Object.keys(e).length > 0) return;
    setBusy(true);
    try {
      await setupBusiness({
        businessName: businessName.trim(),
        language: lang,
        startDate,
        ownerName: ownerName.trim(),
        email: email.trim(),
        password,
      });
      await signIn(email, password);
      onDone();
    } catch (err) {
      setRefusal(err instanceof Refusal ? err.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="yv-auth">
      <form className="yv-auth__panel yv-auth__panel--wide" onSubmit={submit} noValidate>
        <div className="yv-brand">
          <span className="yv-brand__mark" aria-hidden="true">
            Y
          </span>
          YakVender
        </div>
        <h1 className="yv-auth__title">{t('s2.title')}</h1>
        {refusal && <Notice tone="danger">{t(messageKey(refusal))}</Notice>}
        <Field label={t('bizname')} error={errors.businessName}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <Field label={t('bizlang')} hint={t('bizlang.hint')}>
          {() => (
            <Chips
              label={t('bizlang')}
              items={languages.map((l) => ({ id: l, label: LANGUAGE_NAMES[l] }))}
              selected={[lang]}
              onToggle={pickLanguage}
            />
          )}
        </Field>
        <Field label={t('startdate')} hint={t('startdate.hint')} error={errors.startDate}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="date"
              dir="ltr"
              max={businessToday()}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <FormGrid>
          <Field label={t('country')}>{({ id }) => <Input id={id} value={t('algeria')} readOnly />}</Field>
          <Field label={t('currency')}>{({ id }) => <Input id={id} value={t('dzd')} readOnly />}</Field>
          <Field label={t('tz')}>
            {({ id }) => <Input id={id} dir="ltr" value="Africa/Algiers (UTC+1)" readOnly />}
          </Field>
        </FormGrid>
        <h2 className="yv-section__title">{t('ownerblock')}</h2>
        <Field label={t('ownername')} error={errors.ownerName}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              autoComplete="name"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <Field label={t('email')} error={errors.email}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="email"
              dir="ltr"
              inputMode="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <Field label={t('ownerpass')} hint={t('pass.hint')} error={errors.password}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <Button type="submit" variant="main" block busy={busy}>
          {t('create.biz')}
        </Button>
      </form>
    </main>
  );
}
